import { isNative } from "@/lib/platform";

declare global {
  interface Window {
    OneSignalDeferred?: Array<(os: any) => void | Promise<void>>;
  }
}

const ONESIGNAL_APP_ID = "bbfbf693-5e21-4c1f-965e-bf31cf7694dd";
const SAFARI_WEB_ID = "web.onesignal.auto.14c30330-c4fb-483f-9c66-942ad147f917";
const SDK_URL = "https://cdn.onesignal.com/sdks/web/v16/OneSignalSDK.page.js";

let injected = false;
let initQueued = false;

function queueInit() {
  window.OneSignalDeferred = window.OneSignalDeferred || [];
  window.OneSignalDeferred.push(async (OneSignal: any) => {
    await OneSignal.init({
      appId: ONESIGNAL_APP_ID,
      safari_web_id: SAFARI_WEB_ID,
      notifyButton: {
        enable: true,
        size: "medium",
        position: "bottom-right",
        theme: "default",
        showCredit: false,
        colors: {
          "circle.background": "#1A7A4A",
          "circle.foreground": "#ffffff",
          "badge.background": "#22D3EE",
          "badge.foreground": "#0A2E1A",
          "badge.bordercolor": "#ffffff",
          "pulse.color": "#22D3EE",
          "dialog.button.background.hovering": "#0F5A34",
          "dialog.button.background.active": "#0F5A34",
          "dialog.button.background": "#1A7A4A",
          "dialog.button.foreground": "#ffffff",
        },
        text: {
          "tip.state.unsubscribed": "Get FFA alerts for payments, deadlines & prizes",
          "tip.state.subscribed": "You're subscribed to FFA notifications",
          "tip.state.blocked": "You've blocked FFA notifications",
          "message.prenotify": "Tap to get notified about payments, FPL deadlines & prizes",
          "message.action.subscribed": "Thanks! You'll now get FFA push alerts 🎉",
          "message.action.resubscribed": "You're subscribed to FFA notifications",
          "message.action.unsubscribed": "You won't receive FFA push alerts",
          "dialog.main.title": "Fantasy Football Africa Alerts",
          "dialog.main.button.subscribe": "ENABLE ALERTS",
          "dialog.main.button.unsubscribe": "TURN OFF",
          "dialog.blocked.title": "Unblock FFA Notifications",
          "dialog.blocked.message": "Follow these steps to allow notifications from FFA:",
        },
      },
      allowLocalhostAsSecureOrigin: true,
    });
  });
}

function injectSdk() {
  if (injected) return;
  injected = true;
  const s = document.createElement("script");
  s.src = SDK_URL;
  s.async = true;
  document.head.appendChild(s);
}

/**
 * Loads the OneSignal web SDK off the critical path: after `load`, then on idle.
 * Callbacks pushed to window.OneSignalDeferred meanwhile are processed on arrival.
 */
export function loadOneSignalDeferred() {
  if (typeof window === "undefined" || isNative || injected) return;
  window.OneSignalDeferred = window.OneSignalDeferred || [];

  // Queue init synchronously BEFORE any other deferred callback so it always
  // runs first. The hook's login/subscribe callbacks pushed on mount happen
  // after this, guaranteeing init precedes login.
  if (!initQueued) {
    initQueued = true;
    queueInit();
  }

  const schedule = () => {
    const ric = (window as any).requestIdleCallback;
    if (typeof ric === "function") ric(() => injectSdk(), { timeout: 5000 });
    else window.setTimeout(injectSdk, 2500);
  };

  if (document.readyState === "complete") schedule();
  else window.addEventListener("load", schedule, { once: true });
}
